import React, { useState } from "react";
import MaterialTable from "material-table";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Tooltip,
  Checkbox,
} from "@mui/material";
import { icons } from "../../Utilities/UtilitiesWD";
import { useAppMuiTheme } from "../../../theme/MuiThemeBridge";

const stripProductId = (val) => {
  if (typeof val !== "string") return val;
  return val.replace(/\(\d+\)$/, "").replace(/\(null\)$/, "").trim();
};

const DisplayDoors = (props) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [isFindReplaceModalOpen, setFindReplaceModalOpen] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [selectedColumns, setSelectedColumns] = useState({});

  // Mode-aware so the MUI Dialog/Checkbox surfaces inside this table follow
  // the in-app toggle instead of always rendering light.
  const defaultMaterialTheme = useAppMuiTheme();

  const DeleteRow = (row) => {
    const index = row.tableData.id;
    const temp = [...props.doors];
    temp.splice(index, 1);
    props.setDoors(temp);
  };

  const editRow = (row) => {
    props.onEditRow(row);
  };

  const copyRow = (row) => {
    const copiedRow = { ...row };
    props.setDoors((prevDoors) => [...prevDoors, copiedRow]);
  };

  const openFindReplaceModal = () => {
    setFindReplaceModalOpen(true);
  };

  const closeFindReplaceModal = () => {
    setFindReplaceModalOpen(false);
  };

  const handleDeleteRows = () => {
    const updatedDoors = props.doors.filter(
      (door, index) => selectedRows.indexOf(index) === -1
    );
    props.setDoors(updatedDoors);
    setSelectedRows([]);
    closeFindReplaceModal();
  };

  const handleFindReplace = () => {
    selectedRows.forEach((selectedIndex) => {
      const rowData = props.doors[selectedIndex];
      ["QTY", "handing", "stop", "special_instructions"].forEach((key) => {
        if (
          selectedColumns[key] &&
          typeof rowData[key] === "string" &&
          rowData[key] === findText
        ) {
          rowData[key] = replaceText;
        }
      });
    });
    props.setDoors([...props.doors]);
    closeFindReplaceModal();
  };

  const customLabels = {
    quantity: "QTY",
    handing: "Handing",
    casing_type: "Casing",
    width: "Dimensions",
    height: "Dimensions",
    slab_style: "Slab Style",
    size_jamb_type: "Jamb",
    hinge_finish: "Hinge Finish",
    stop: "Stop",
    estimated_price: "Estimated Price",
    margin: "Margin %",
  };

  const getCasingAppliedStatus = (rowData) => {
    if (rowData.applied === "Yes") return " (Applied)";
    if (rowData.applied === "No") return " (Not Applied)";
    return "";
  };

  const renderFindOptions = () => {
    const commonFields = [
      "quantity",
      "handing",
      "casing_type",
      "slab_style",
      "width",
      "size_jamb_type",
      "hinge_finish",
      "stop",
      "estimated_price",
      "catch_astragal",
    ];

    return commonFields.flatMap((field) => {
      const values = new Set();
      selectedRows.forEach((selectedIndex) => {
        const rowData = props.doors[selectedIndex];
        if (field === "width" || field === "height") {
          values.add(`${rowData.width}x${rowData.height}`);
        } else if (field === "casing_type") {
          values.add(
            `${rowData.casing_type}${getCasingAppliedStatus(rowData)}`
          );
        } else {
          values.add(rowData[field]);
        }
      });

      return Array.from(values).map((value, index) => (
        <option key={`${field}-${index}`} value={`${field}: ${value}`}>
          {`${customLabels[field] || field}: ${stripProductId(String(value ?? ""))}`}
        </option>
      ));
    });
  };

  const handleColumnCheckboxChange = (event, columnName) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [columnName]: event.target.checked,
    }));
  };

  const appliedBadge = (label) => (
    <span className="mt-1 inline-block w-fit rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
      {label}
    </span>
  );

  return (
    <div className="app-card mt-5 overflow-hidden rounded-2xl border">
      <ThemeProvider theme={defaultMaterialTheme}>
        {/* Transparent so the card (translucent under glass) shows through. */}
        <MaterialTable
          style={{
            backgroundColor: "transparent",
            boxShadow: "none",
            color: "var(--app-text)",
            fontSize: "13px",
          }}
          columns={[
            {
              title: "QTY",
              field: "quantity",
              width: 60,
              cellStyle: { textAlign: "center", fontWeight: 600 },
              headerStyle: { textAlign: "center" },
            },
            {
              title: "Handing",
              field: "handing",
              render: (rowData) => {
                if (rowData.door_type === "bifold") return "Bifold";
                if (rowData.door_type === "double") return "Double";
                return rowData.handing;
              },
            },
            {
              title: "Casing",
              field: "casing_type",
              render: (rowData) => {
                const casing = stripProductId(rowData.casing_type);
                if (casing === "N/A") return "N/A";
                return (
                  <div className="flex flex-col">
                    <span>{casing}</span>
                    {rowData.applied === "Yes" && appliedBadge("Applied")}
                    {rowData.applied === "No" && appliedBadge("Not Applied")}
                    {rowData.applied === "Loose" && appliedBadge("Loose")}
                    {rowData.applied === "Loose Hooped" && appliedBadge("Loose Hooped")}
                  </div>
                );
              },
            },
            {
              title: "Slab Information",
              field: "slab_style",
              render: (rowData) => {
                const w = stripProductId(rowData.width);
                const s = stripProductId(rowData.slab_style);
                return (
                  <div className="flex flex-col">
                    <span className="font-medium">{rowData.core} {s}</span>
                    <span className="app-muted text-[11px]">
                      {w} x {rowData.height}
                    </span>
                  </div>
                );
              },
            },
            {
              title: "Jamb",
              field: "size_jamb_type",
              render: (rowData) => stripProductId(rowData.size_jamb_type),
            },
            {
              title: "Hinge",
              field: "hinge_finish",
              render: (rowData) => stripProductId(rowData.hinge_finish),
            },
            {
              title: "Stop",
              field: "stop",
              render: (rowData) => {
                const stop = stripProductId(rowData.stop);
                if (stop === "N/A") return "N/A";
                return (
                  <div className="flex flex-col">
                    <span>{stop}</span>
                    {rowData.stop_applied === "Yes" && appliedBadge("Applied")}
                    {rowData.stop_applied === "No - Loose" && appliedBadge("Loose")}
                  </div>
                );
              },
            },
            {
              title: "Price",
              field: "estimated_price",
              cellStyle: { textAlign: "right" },
              headerStyle: { textAlign: "right" },
              render: (rowData) => (
                <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-300">
                  ${rowData.estimated_price}
                </span>
              ),
            },
            {
              title: "Instructions",
              field: "special_instructions",
              cellStyle: { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
              render: (rowData) =>
                rowData.special_instructions ? (
                  <Tooltip title={rowData.special_instructions}>
                    <span className="app-muted">
                      {rowData.special_instructions}
                    </span>
                  </Tooltip>
                ) : null,
            },
            {
              title: "",
              field: "action",
              sorting: false,
              width: 120,
              cellStyle: { textAlign: "center", paddingRight: 8 },
              render: (rowData) => (
                <div className="flex items-center justify-center gap-1">
                  <Tooltip title="Edit">
                    <button
                      type="button"
                      aria-label="Edit door"
                      className="app-icon-btn h-9 w-9 !text-sky-600 dark:!text-sky-300"
                      onClick={() => editRow(rowData)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </Tooltip>
                  <Tooltip title="Copy">
                    <button
                      type="button"
                      aria-label="Copy door"
                      className="app-icon-btn h-9 w-9"
                      onClick={() => copyRow(rowData)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                      </svg>
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <button
                      type="button"
                      aria-label="Delete door"
                      className="app-icon-btn h-9 w-9 !text-rose-500 dark:!text-rose-300"
                      onClick={() => DeleteRow(rowData)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </Tooltip>
                </div>
              ),
            },
          ]}
          components={{
            Toolbar: (toolbarProps) => {
              if (selectedRows.length > 0) {
                return (
                  <div className="app-divider flex flex-wrap items-center gap-2 border-b bg-sky-500/10 px-4 py-2.5">
                    <span className="app-accent mr-auto text-xs font-semibold">
                      {selectedRows.length} row{selectedRows.length > 1 ? "s" : ""} selected
                    </span>
                    <button
                      type="button"
                      className="app-btn app-btn-primary app-btn-sm"
                      onClick={openFindReplaceModal}
                    >
                      Find & Replace
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-danger app-btn-sm"
                      onClick={handleDeleteRows}
                    >
                      Delete Selected
                    </button>
                  </div>
                );
              }
              return null;
            },
          }}
          data={props.doors}
          title="Doors"
          icons={icons}
          options={{
            pageSize: 10,
            emptyRowsWhenPaging: false,
            pageSizeOptions: [5, 10, 15, 25],
            // Theme tokens (CSS vars) so the table follows all four themes.
            headerStyle: {
              backgroundColor: "var(--app-surface-bg)",
              color: "var(--app-text-muted)",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.02em",
              borderBottom: "1px solid var(--app-divider)",
            },
            searchFieldStyle: {
              color: "var(--app-text)",
              fontSize: "13px",
            },
            rowStyle: () => ({
              fontSize: 13,
              backgroundColor: "transparent",
            }),
          }}
        />
      </ThemeProvider>

      {/* Find & Replace Dialog */}
      <Dialog
        open={isFindReplaceModalOpen}
        onClose={closeFindReplaceModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Find & Replace
          <span className="app-muted mt-0.5 block text-xs font-normal">
            Replace values in selected rows
          </span>
        </DialogTitle>

        <DialogContent>
          <div className="space-y-4 pt-1">
            {/* Column checkboxes */}
            {props.doors.length > 0 && (
              <div>
                <p className="mb-2 block text-sm font-medium">
                  Columns to search
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {["QTY", "handing", "stop", "special_instructions"].map(
                    (col) => (
                      <label
                        key={col}
                        className="flex cursor-pointer items-center gap-1 text-sm"
                      >
                        <Checkbox
                          size="small"
                          checked={!!selectedColumns[col]}
                          onChange={(e) => handleColumnCheckboxChange(e, col)}
                        />
                        <span>{col.replace(/_/g, " ")}</span>
                      </label>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Find dropdown */}
            {selectedRows.length > 0 && (
              <div>
                <label
                  htmlFor="door-find"
                  className="mb-1 block text-sm font-medium"
                >
                  Find
                </label>
                <select
                  id="door-find"
                  className="app-input w-full rounded-xl border px-3 py-2.5 text-sm"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                >
                  <option value="">Select a value...</option>
                  {renderFindOptions()}
                </select>
              </div>
            )}

            {/* Replace input */}
            <div>
              <label
                htmlFor="door-replace"
                className="mb-1 block text-sm font-medium"
              >
                Replace with
              </label>
              <input
                id="door-replace"
                type="text"
                className="app-input w-full rounded-xl border px-3 py-2.5 text-sm"
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                placeholder="Enter replacement value..."
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={closeFindReplaceModal}
          >
            Cancel
          </button>
          <button
            type="button"
            className="app-btn app-btn-primary"
            onClick={handleFindReplace}
          >
            Replace
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DisplayDoors;
